import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, FormGroup, Label, Input } from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import axios from 'axios';
import { Autocomplete } from '@react-google-maps/api';
import { setActiveFlow, setCurrentStep, setIsPickupRequired, setFormData } from 'store/slices/flowSlice';
import { countryOptions } from 'data/countries';
import { Toggle } from 'rsuite';
import { useTranslation } from "react-i18next";

const GEONAMES_USERNAME = 'webnaitra';

const OceanForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
const validationSchema = Yup.object({
  fromLocation: Yup.object().required(t('From location is required')),
  fromAddress: Yup.string().when('pickupRequired', {
    is: true,
    then: () => Yup.string().required(t('Address is required when pickup is required')),
    otherwise: () => Yup.string().nullable(),
  }),
  fromSeaport: Yup.object().when('pickupRequired', {
    is: false,
    then: () => Yup.object().required(t('From seaport is required when pickup is not required')),
    otherwise: () => Yup.object().nullable(),
  }),
  toLocation: Yup.object().required(t('To location is required')),
  toAddress: Yup.string().when('deliveredToRecipient', {
    is: true,
    then: () => Yup.string().required(t('Address is required when delivery to recipient is required')),
    otherwise: () => Yup.string().nullable(),
  }),
  toSeaport: Yup.object().when('deliveredToRecipient', {
    is: false,
    then: () => Yup.object().required(t('To seaport is required when delivery to recipient is not required')),
    otherwise: () => Yup.object().nullable(),
  }),
});

  const [pickupRequired, setPickupRequired] = useState(true);
  const [deliveredToRecipient, setDeliveredToRecipient] = useState(true);
  const [selectedFromCountry, setSelectedFromCountry] = useState(null);
  const [selectedToCountry, setSelectedToCountry] = useState(null);
  const [fromSeaports, setFromSeaports] = useState([]);
  const [toSeaports, setToSeaports] = useState([]);
  const fromAddressRef = useRef(null);
  const toAddressRef = useRef(null);
  const seaportCountries = countryOptions.filter(country => country.seaport);

  const handleSubmit = (values) => {
    dispatch(setActiveFlow('ocean'));
    dispatch(setCurrentStep('1'));
    dispatch(setIsPickupRequired(pickupRequired));
    dispatch(setFormData(values));
  };

  const fetchSeaports = async (countryCode, setSeaports) => {
    try {
      const response = await axios.get(`http://api.geonames.org/searchJSON`, {
        params: {
          country: countryCode,
          featureClass: 'L',
          featureCode: 'PRT',
          username: GEONAMES_USERNAME,
          maxRows: 50,
        },
      });

      const seaportOptions = response.data.geonames.map(seaport => ({
        value: seaport.geonameId,
        label: `${seaport.name} (${seaport.countryName})`,
      }));

      setSeaports(seaportOptions);
    } catch (error) {
      console.error('Error fetching seaports: ', error);
      setSeaports([]);
    }
  };

  const handleCountryChange = (field, value, setFieldValue) => {
    setFieldValue(field, value);
    const countryCode = value ? value.value : null;
    if (field === 'fromLocation') {
      setSelectedFromCountry(countryCode);
      setFieldValue('fromSeaport', null); // Clear previous seaport selection
      if (countryCode) {
        fetchSeaports(countryCode, setFromSeaports);
      }
    } else if (field === 'toLocation') {
      setSelectedToCountry(countryCode);
      setFieldValue('toSeaport', null); // Clear previous seaport selection
      if (countryCode) {
        fetchSeaports(countryCode, setToSeaports);
      }
    }
  };

  const handlePlaceSelect = (autocomplete, setFieldValue, field) => {
    const place = autocomplete.getPlace();
    if (place && place.formatted_address) {
      sessionStorage.setItem(field, place?.formatted_address);
      setFieldValue(field, place.formatted_address); // Set the full address
    } else {
      alert('Unable to retrieve a valid address. Please try selecting again.');
    }
  };

  return (
    <Formik
      initialValues={{ 
        fromLocation: '', 
        fromAddress: '', 
        toLocation: '', 
        toAddress: '',
        deliveredToRecipient: true,
        pickupRequired: true
       }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, errors, touched, values }) => (
        <Form>
          <Row>
            <Col sm="12">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label mb-2 fs-custom fw-bold text-dark e2g-form-size" for="fromLocation">{t("From")}</Label>
                <FormGroup switch>
                  <Toggle
                    checked={pickupRequired}
                    onChange={(value) => {
                      setFieldValue('pickupRequired', value);
                      setPickupRequired(value);
                    }}
                  />
                  <Label check className='fs-7'>{t("Pickup Required")}</Label>
                </FormGroup>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Select
                  className='bg-white rounded e2g-custom-shadow border-0 e2g-form-input'
                  id="fromLocation"
                  options={seaportCountries}
                  onChange={option => handleCountryChange('fromLocation', option, setFieldValue)}
                  placeholder={t("Select Location")}
                />
                {errors.fromLocation && touched.fromLocation ? (
                  <div className="e2g-error-text text-danger">{t(errors.fromLocation)}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                {pickupRequired ? (
                  <Autocomplete
                    key={'from'+values?.fromLocation?.value}
                    onLoad={(autocomplete) => (fromAddressRef.current = autocomplete)}
                    onPlaceChanged={() =>
                      handlePlaceSelect(fromAddressRef.current, setFieldValue, 'fromAddress')
                    }
                    options={{
                      types: ['address'], // Restrict to address results
                      componentRestrictions: {
                        country: values.fromLocation ? values?.fromLocation?.value : '',
                      },
                    }}
                  >
                    <Field
                      name="fromAddress"
                      as={Input}
                      placeholder={t("Provide Full Address")}
                      disabled={!(values?.fromLocation?.value)}
                    />
                  </Autocomplete>
                ) : (
                  <Select
                    className='bg-white rounded e2g-custom-shadow border-0'
                    id="fromSeaport"
                    options={fromSeaports}
                    onChange={option => setFieldValue('fromSeaport', option)}
                    placeholder={t("Select Seaport")}
                    isDisabled={!selectedFromCountry}
                  />
                )}
                {errors.fromAddress && touched.fromAddress ? (
                  <div className="e2g-error-text text-danger">{t(errors.fromAddress)}</div>
                ) : null}
                {errors.fromSeaport && touched.fromSeaport ? (
                  <div className="e2g-error-text text-danger">{t(errors.fromSeaport)}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <FormGroup className="e2g-cargo-form-group">
                <Label className="e2g-cargo-form-label mb-2 fs-custom fw-bold text-dark e2g-form-size" for="toLocation">{t("To")}</Label>
                <FormGroup switch>
                  <Toggle
                    checked={deliveredToRecipient}
                    onChange={(value) => {
                      setFieldValue('deliveredToRecipient', value);
                      setDeliveredToRecipient(value);
                    }}
                  />
                  <Label check className='fs-7'>{t("Delivered to Recipient")}</Label>
                </FormGroup>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                <Select
                  className='bg-white rounded e2g-custom-shadow border-0 e2g-form-input'
                  id="toLocation"
                  options={seaportCountries}
                  onChange={option => handleCountryChange('toLocation', option, setFieldValue)}
                  placeholder={t("Select Location")}
                />
                {errors.toLocation && touched.toLocation ? (
                  <div className="e2g-error-text text-danger">{t(errors.toLocation)}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup className="e2g-cargo-form-group">
                {deliveredToRecipient ? (
                  <Autocomplete
                    key={'to'+values?.toLocation?.value}
                    onLoad={(autocomplete) => (toAddressRef.current = autocomplete)}
                    onPlaceChanged={() =>
                      handlePlaceSelect(toAddressRef.current, setFieldValue, 'toAddress')
                    }
                    options={{
                      types: ['address'], // Restrict to address results
                      componentRestrictions: {
                        country: values.toLocation ? values?.toLocation?.value : '',
                      },
                    }}
                  >
                    <Field
                      name="toAddress"
                      as={Input}
                      placeholder={t("Provide Full Address")}
                      disabled={!(values?.toLocation?.value)}
                    />
                  </Autocomplete>
                ) : (
                  <Select
                    className='bg-white rounded e2g-custom-shadow border-0'
                    id="toSeaport"
                    options={toSeaports}
                    onChange={option => setFieldValue('toSeaport', option)}
                    placeholder={t("Select Seaport")}
                    isDisabled={!selectedToCountry}
                  />
                )}
                {errors.toAddress && touched.toAddress ? (
                  <div className="e2g-error-text text-danger">{t(errors.toAddress)}</div>
                ) : null}
                {errors.toSeaport && touched.toSeaport ? (
                  <div className="e2g-error-text text-danger">{t(errors.toSeaport)}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm="12" className='text-end e2g-form-btn'>
              <Button type="submit" color="dark" className="btn btn-dark rounded-pill py-2 px-4 e2g-land-btn">
                {t("Get a spot offer")}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default OceanForm;